import { useEffect, useRef, useState } from 'react'

type Options = {
  /** When true, fires a flash on the initial mount instead of skipping it.
   *  Use case: highlighting brand-new entities that appear in a list after
   *  page load. Caller decides whether THIS mount counts as "new" (typically
   *  by comparing the entity's id against a snapshot of ids that existed
   *  when the page first loaded). */
  flashOnFirstMount?: boolean
  /** Flash duration in milliseconds. Defaults to 2500. */
  durationMs?: number
}

/** Returns `true` for `durationMs` after `value` changes from its previous
 *  render value. Skips the initial mount by default so fields don't all
 *  flash when the page first paints.
 *
 *  Optional `identityKey` — pass something stable per "entity" (e.g.
 *  `matter.id`) when the same component instance is reused across different
 *  entities. When the identityKey changes the hook treats it as a silent
 *  reset — the value swap is a navigation, not a real field update.
 *
 *  Options:
 *    `flashOnFirstMount` — fire on initial render (for newly-arrived rows).
 *    `durationMs`        — how long the flash lasts (default 2500ms).
 *
 *  Compares with `!==` — works for strings/numbers/booleans/null. For arrays
 *  or objects, pass a stable scalar representation (length, JSON.stringify).
 */
export function useFlashOnChange<T>(
  value: T,
  identityKey?: unknown,
  opts: Options = {},
): boolean {
  const { flashOnFirstMount = false, durationMs = 2500 } = opts
  const prevRef = useRef<T>(value)
  const prevIdRef = useRef<unknown>(identityKey)
  const firstRunRef = useRef(true)
  const [flashing, setFlashing] = useState(flashOnFirstMount)
  useEffect(() => {
    // Identity changed — caller swapped to a different entity. Reset state
    // without flashing; the value diff that's about to be seen is from
    // entity A's value to entity B's value, not a real update.
    if (identityKey !== prevIdRef.current) {
      prevIdRef.current = identityKey
      prevRef.current = value
      firstRunRef.current = false
      setFlashing(false)
      return
    }
    if (firstRunRef.current) {
      firstRunRef.current = false
      prevRef.current = value
      if (flashOnFirstMount) {
        // setFlashing(true) is already set by initial state; we just need
        // a timer to clear it.
        const t = setTimeout(() => setFlashing(false), durationMs)
        return () => clearTimeout(t)
      }
      return
    }
    if (prevRef.current !== value) {
      prevRef.current = value
      setFlashing(true)
      const t = setTimeout(() => setFlashing(false), durationMs)
      return () => clearTimeout(t)
    }
  }, [value, identityKey, durationMs, flashOnFirstMount])
  return flashing
}
